import { useEffect, useRef } from 'react';
import { ErrorState, Loading } from './components/common';
import { useHistory, useLeague, usePlayers } from './lib/api';
import { inauguralSeason } from './lib/league';
import { Link, matchParam, matchSegments, useRoute } from './lib/router';
import Assets from './views/Assets';
import Contention from './views/Contention';
import DraftRecap from './views/DraftRecap';
import History from './views/History';
import Home from './views/Home';
import Luck from './views/Luck';
import Managers from './views/Managers';
import Playoffs from './views/Playoffs';
import Records from './views/Records';
import Rivalries from './views/Rivalries';
import Schedule from './views/Schedule';
import Standings from './views/Standings';
import TeamDetail from './views/TeamDetail';
import Teams from './views/Teams';
import Transactions from './views/Transactions';
import WeekRecap from './views/WeekRecap';

// Labels are kept short deliberately: at this many entries, full names like
// "Transactions" and "Contention Window" overflow the nav on a laptop. The nav
// is its own horizontal scroller, so it shrinks rather than pushing the page
// wide, but a label that has to be scrolled to is a label nobody reads.
const NAV = [
  { to: '/', label: 'Home' },
  { to: '/managers', label: 'Managers' },
  { to: '/schedule', label: 'Schedule' },
  { to: '/standings', label: 'Standings' },
  { to: '/playoffs', label: 'Odds' },
  { to: '/teams', label: 'Teams' },
  { to: '/draft', label: 'Draft' },
  { to: '/assets', label: 'Picks' },
  { to: '/transactions', label: 'Trades' },
  { to: '/contention', label: 'Window' },
  { to: '/records', label: 'Records' },
  { to: '/luck', label: 'Luck' },
  { to: '/rivalries', label: 'Rivalries' },
  { to: '/history', label: 'History' },
];

function isActive(path: string, to: string): boolean {
  return to === '/' ? path === '/' : path.startsWith(to);
}

/** Routes whose content is a grid as wide as the league has teams. */
const WIDE_ROUTES = new Set(['/draft', '/rivalries']);

/**
 * Keeps the horizontally-scrolling nav honest on a phone.
 *
 * Two jobs the CSS cannot do. First, centre the active tab: below ~1100px only
 * a handful of the tabs are on screen, so deep-linking to /history parks the rail
 * at Home with the orange chip off-screen -- the site looks like it lost your
 * place. Second, publish whether content is hidden left/right, which the edge
 * fades in styles.css read off `data-more-left` / `data-more-right`. The
 * scrollbar is hidden and touch devices never draw one, so without this there
 * is nothing at all on screen saying the rail scrolls.
 *
 * Scrolling is done by assigning `scrollLeft` rather than `scrollIntoView`,
 * which walks up the ancestor chain and would scroll the PAGE vertically as a
 * side effect of a purely horizontal correction.
 */
function useNavRail(path: string) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const nav = ref.current;
    if (!nav) return;

    const sync = () => {
      // 2px of slack: fractional scroll positions on a HiDPI screen never land
      // exactly on 0 or on scrollWidth - clientWidth, which would leave one
      // fade stuck on at a hard stop.
      nav.dataset.moreLeft = nav.scrollLeft > 2 ? '1' : '0';
      nav.dataset.moreRight =
        nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 2 ? '1' : '0';
    };

    const centreActive = () => {
      const active = nav.querySelector<HTMLElement>('a.active');
      if (!active) return;
      const rail = nav.getBoundingClientRect();
      const tab = active.getBoundingClientRect();
      nav.scrollLeft += tab.left - rail.left - (rail.width - tab.width) / 2;
      sync();
    };

    centreActive();
    // Barlow Condensed and Inter arrive from Google Fonts after first paint and
    // are narrower than the fallbacks, so every measurement above is taken
    // against the wrong widths on a cold load. Re-run once they land.
    void document.fonts?.ready.then(centreActive);

    nav.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      nav.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [path]);

  return ref;
}

/**
 * `since` is null until the history walk lands, and the sub-line simply omits
 * the year until then. The alternative -- defaulting to the season in progress
 * -- would render "Est. 2026" on a five-year-old league for as long as the
 * fetch takes, and a wrong fact that corrects itself is worse than a late one.
 */
function Masthead({ leagueName, since }: { leagueName: string | undefined; since: string | null }) {
  const path = useRoute();
  const navRef = useNavRail(path);

  return (
    <header className="masthead">
      <div className="wrap masthead-inner">
        <Link to="/" className="brand">
          <img className="brand-mark" src="/favicon.svg" alt="" width={32} height={32} />
          <span className="brand-text">
            <span className="brand-name">{leagueName ?? 'Dynasty League'}</span>
            <span className="brand-sub">Sleeper Dynasty{since && ` · Est. ${since}`}</span>
          </span>
        </Link>
        <nav className="nav" ref={navRef} aria-label="League sections">
          {NAV.map((item) => (
            <Link key={item.to} to={item.to} className={isActive(path, item.to) ? 'active' : ''}>
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
}

export default function App() {
  const path = useRoute();
  const league = useLeague();
  const history = useHistory();
  const players = usePlayers();

  // The player dictionary and history are enhancements, not blockers -- the
  // league bundle is the only hard dependency for rendering anything.
  const body = (() => {
    if (league.loading) return <Loading />;
    if (league.error || !league.data) {
      return <ErrorState message={league.error ?? 'No data returned'} onRetry={league.reload} />;
    }

    const bundle = league.data;
    const teamId = matchParam(path, '/teams');
    if (teamId !== null) {
      return (
        <TeamDetail bundle={bundle} players={players.data} rosterId={Number(teamId)} />
      );
    }

    // /recap/:season/:week. History is optional -- it only makes archived
    // seasons reachable, and the view rejects a season it cannot resolve.
    const recap = matchSegments(path, '/recap', 2);
    if (recap !== null) {
      const [season, week] = recap;
      if (/^\d{4}$/.test(season) && /^\d{1,2}$/.test(week)) {
        if (history.loading) return <Loading label="Loading league history" />;
        return (
          <WeekRecap
            bundle={bundle}
            history={history.data}
            players={players.data}
            season={season}
            week={Number(week)}
          />
        );
      }
    }

    switch (path) {
      case '/':
        return (
          <Home bundle={bundle} history={history.data} historyLoading={history.loading} />
        );
      case '/standings':
        return <Standings bundle={bundle} />;
      case '/schedule':
        // History is optional -- it only supplies the preseason opponent-level
        // prior; the fixture list itself is entirely current-season.
        return <Schedule bundle={bundle} history={history.data} />;
      case '/playoffs':
        // History is optional -- it supplies the preseason strength prior, and
        // without it every team simply starts at the league average.
        return <Playoffs bundle={bundle} history={history.data} />;
      case '/teams':
        return <Teams bundle={bundle} players={players.data} />;
      case '/managers':
        // History is optional in the type but load-bearing in practice: without
        // it there is exactly one league year, so every career reads as one
        // season. Blocked on the walk for the same reason /records is -- cards
        // that answer "all-time" from the season in progress and then correct
        // themselves are worse than cards that arrive late.
        if (history.loading) return <Loading label="Loading league history" />;
        return <Managers bundle={bundle} history={history.data} />;
      case '/draft':
        return <DraftRecap bundle={bundle} />;
      case '/assets':
        // History is optional -- it only widens the trade-tree search to past
        // league years. The pick grid itself is entirely current-season.
        return <Assets bundle={bundle} history={history.data} />;
      case '/transactions':
        // History is optional -- it only widens the trade ledger and the player
        // trees to past league years. The move feed is entirely current-season.
        return <Transactions bundle={bundle} history={history.data} players={players.data} />;
      case '/contention':
        return <Contention bundle={bundle} players={players.data} />;
      case '/records':
        if (history.loading) return <Loading label="Loading league history" />;
        return <Records bundle={bundle} history={history.data} />;
      case '/luck':
        // History is optional -- it only supplies era-correct names for
        // managers who have since left the league.
        return <Luck bundle={bundle} history={history.data} players={players.data} />;
      case '/rivalries':
        if (history.loading) return <Loading label="Loading league history" />;
        return <Rivalries bundle={bundle} history={history.data} />;
      case '/history':
        if (history.loading) return <Loading label="Loading league history" />;
        if (history.error || !history.data) {
          return (
            <ErrorState message={history.error ?? 'No history returned'} onRetry={history.reload} />
          );
        }
        return <History history={history.data} />;
      default:
        return (
          <div className="center-state">
            <div style={{ fontFamily: 'var(--display)', fontSize: 46 }}>404</div>
            <div>No such page.</div>
            <Link to="/" className="retry">
              Back to the league
            </Link>
          </div>
        );
    }
  })();

  return (
    <div className="shell">
      <Masthead
        leagueName={league.data?.league.name}
        since={league.data ? inauguralSeason(league.data, history.data) : null}
      />
      <main className={`wrap${WIDE_ROUTES.has(path) ? ' wrap-wide' : ''}`}>{body}</main>
      <footer className="foot">
        <div className="wrap foot-inner">
          <span>
            Data from the{' '}
            <a href="https://docs.sleeper.com/" target="_blank" rel="noopener noreferrer">
              Sleeper API
            </a>
            . Not affiliated with Sleeper or the NFL.
          </span>
          {league.data && (
            <a
              href={`https://sleeper.com/leagues/${league.data.league.league_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Sleeper
            </a>
          )}
        </div>
      </footer>
    </div>
  );
}
